import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { Decoration } from '@tiptap/pm/view';

import { FlowDBlockNodeView } from './FlowDBlockNodeView';

interface DBlockOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    dBlock: {
      /**
       * Toggle a dBlock
       */
      setDBlock: (position?: number) => ReturnType;
    };
  }
}

export const FlowDBlock = Node.create<DBlockOptions>({
  name: 'dBlock',

  group: 'block',

  content: 'block',

  draggable: true,

  addAttributes() {
    return {
      personaId: {},
      blockId: {},
      blockType: {},
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="d-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'd-block' }), 0];
  },

  addNodeView() {
    // Each keystroke is one transaction that rebuilds the whole document, and
    // Tiptap's default node view re-renders on any transaction, so every bubble
    // redrew. Re-render a bubble only when what it shows changed: its node
    // (content, persona, marks via `Node.eq`) or its selection decoration. The
    // edited bubble still re-renders; this spares the untouched ones.
    return ReactNodeViewRenderer(FlowDBlockNodeView, {
      update({ oldNode, newNode, oldDecorations, newDecorations, updateProps }) {
        const unchanged = oldNode.eq(newNode) && sameDecorations(oldDecorations, newDecorations);
        if (!unchanged) {
          updateProps();
        }
        return true;
      },
    });
  },
});

// Outer decorations arrive as a fresh array each transaction, so compare by
// value: same length and same decoration instances (ProseMirror reuses the
// instances for decorations that did not change).
function sameDecorations(a: readonly Decoration[], b: readonly Decoration[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((decoration, index) => decoration === b[index]);
}
